/**
 * The last look before the rows are written.
 *
 * Classification used to happen invisibly at the end of processing: a person
 * confirmed a mapping and the next thing they saw was a file that had already
 * decided what every row meant. This screen is the one moment where looking is
 * still cheap.
 */

import { useFollowRecordModule, useModule } from '../modules/useModule';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useToast } from '../../provider/toast';
import {
	useClassificationPreviewQuery,
	useFileQuery,
	useStartProcessingMutation,
} from '../../queries/intake';
import { PageHeader } from '../../provider/pageTitle';
import { useClientCrumbs } from './crumbs';
import { Card, ErrorNotice, Note, StatusBadge } from '../../shared/ui';
import { useCanonicalFieldsQuery } from '../../queries/firm';
import { DataTable, type Column } from '../shared/tables';
import { ErrorState, LoadingState } from '../shared/states';
import { count } from '../../utils/format';
import type { PreviewRow, PreviewRule } from '../../schemas';
import { Button } from '@mui/material';
import { PreparingRowsDialog } from './PreparingRowsDialog';

/** "document type is ZTNR", "customer contains EXPORT" — the rule as a
 *  sentence, because `ENDS_WITH` and a raw column name are not what a person
 *  wrote down when they decided what a return looks like. */
function ruleSentence(
	rule: PreviewRule,
	labelOf: (key: string) => string,
): string {
	const field = labelOf(rule.matchField);
	const verb =
		rule.matchOperator === 'CONTAINS'
			? 'contains'
			: rule.matchOperator === 'STARTS_WITH'
				? 'starts with'
				: rule.matchOperator === 'ENDS_WITH'
					? 'ends with'
					: 'is';
	return `${field} ${verb} ${rule.matchValue}`;
}

export default function ReviewRules() {
	const { clientId = '', fileId = '' } = useParams();
	const fields = useCanonicalFieldsQuery();
	const labelOf = (key: string) =>
		fields.data?.fields.find((field) => field.field === key)?.label ??
		key.replace(/_/g, ' ');
	const navigate = useNavigate();

	/* Still MAPPING_CONFIRMED means the rows are being prepared: re-read the
     file until it is NORMALISED, and hold the page behind the dialog. */
	const file = useFileQuery(
		fileId ?? null,
		(status) => status === 'MAPPING_CONFIRMED',
	);
	const preparing = file.data?.status === 'MAPPING_CONFIRMED';
	/* The file's module wins over the URL's, for the badge and every link. */
	useFollowRecordModule(file.data?.module);
	const clientCrumbs = useClientCrumbs(clientId, file.data?.module);
	const { link } = useModule();
	const normaliseRunId = useSearchParams()[0].get('normalise');

	/* Every rule in scope starts selected. Opt-out rather than opt-in: the
     firm published these rules precisely so they would run, and a screen
     that starts with nothing ticked invites someone to process a file
     against no rules at all and wonder later why every row is
     unclassified. */
	const [chosen, setChosen] = useState<Set<string> | null>(null);

	/* The preview endpoint reads an absent `ruleIds` as "every rule in scope"
     — that is how the first call learns what the scope even is. So an empty
     selection cannot be asked about: the request that describes it is
     byte-identical to the request that means the opposite, and the answer
     would show every rule claiming rows while every box sits unticked.
     Nothing is asked in that state; what it would produce is rendered from
     what is already known. */
	const asking = chosen === null || chosen.size > 0;

	const query = useMemo(() => {
		if (!chosen) return '';
		return [...chosen]
			.map((id) => `ruleIds=${encodeURIComponent(id)}`)
			.join('&');
	}, [chosen]);

	/* The preview reads the prepared rows: not asked for until they exist. */
	const preview = useClassificationPreviewQuery(
		fileId ?? null,
		query,
		asking && !preparing,
	);

	/* The query is disabled when nothing is ticked, so the scope and
     the sample are mirrored here. They are properties of the file and its
     firm, not of the current selection, and the screen still has to draw
     them while no request is in flight. */
	const [scope, setScope] = useState<PreviewRule[]>([]);
	const [sample, setSample] = useState<PreviewRow[]>([]);
	const [columns, setColumns] = useState<string[]>([]);
	const [sampleSize, setSampleSize] = useState(0);

	useEffect(() => {
		if (!preview.data) return;
		setScope(preview.data.rules);
		setSample(preview.data.rows);
		setColumns(preview.data.columns);
		setSampleSize(preview.data.sampleSize);
		/* Seed the selection from the first answer, which is also the only call
       that reports every rule in scope rather than the filtered set. */
		if (chosen === null) {
			setChosen(new Set(preview.data.rules.map((rule) => rule.id)));
		}
	}, [chosen, preview.data]);

	const start = useStartProcessingMutation(fileId ?? '');
	const toast = useToast();

	const startProcessing = () =>
		start.mutate(chosen ? [...chosen] : [], {
			onSuccess: (run) => {
				toast(
					'ok',
					'Processing started. The rules you chose will classify the rows as they land.',
				);
				/* Straight to the run, carrying its id. Landing on the file page
           instead is what left an operator staring at a status badge for
           thirteen minutes with no way to tell work from a hang — the screen
           that answers that now exists, so this is where the button goes. */
				navigate(
					link(
						`/intake/clients/${clientId}/files/${fileId}/processing?run=${encodeURIComponent(run.runId)}`,
					),
				);
			},
		});

	if (file.isPending) return <LoadingState label="Loading the file…" />;
	if (file.error)
		return <ErrorState error={file.error} onRetry={file.refetch} />;
	if (!file.data) return null;

	/* With nothing ticked there is no answer from the server, so the rules
     are shown claiming nothing and the rows as the unclassified they would
     be. The scope itself still comes from the last real answer — unticking
     a rule does not remove it from the firm. */
	const rules = asking
		? (preview.data?.rules ?? scope)
		: scope.map((rule) => ({ ...rule, matchCount: 0 }));
	const rows = asking
		? (preview.data?.rows ?? sample)
		: sample.map((row) => ({ ...row, ruleId: null, classification: null }));
	/* How many rows the endpoint was asked for, mirrored the same way `scope`/
     `sample`/`columns` are: a request for "nothing chosen" clears
     `preview.data`, and the number asked for is still a property of the file
     and its firm, not of the current selection. */
	const askedFor = asking
		? (preview.data?.sampleSize ?? sampleSize)
		: sampleSize;

	/* Only the columns the chosen rules actually look at, plus enough
     identification to recognise a row. A twenty-seven column table would
     bury the one value the decision turns on. */
	const shown = new Set<string>([
		'invoice_number',
		...rules.map((rule) => rule.matchField),
	]);
	const shownIndexes = columns
		.map((name, index) => ({ name, index }))
		.filter((entry) => shown.has(entry.name));

	const tableColumns: Array<Column<PreviewRow>> = [
		...shownIndexes.map(({ name, index }) => ({
			key: name,
			label: labelOf(name),
			render: (row: PreviewRow) => {
				const value = row.values[index];
				return value === null || value === undefined ? (
					<span className="mut">—</span>
				) : (
					String(value)
				);
			},
		})),
		{
			key: 'classification',
			label: 'Becomes',
			render: (row: PreviewRow) =>
				row.classification ? (
					<StatusBadge
						group="classification"
						value={row.classification}
					/>
				) : (
					/* Not an error and not a blank: no rule claimed this row, which is
             a real outcome a person may well want to go and fix. */
					<StatusBadge group="classification" value="UNKNOWN" />
				),
		},
		{
			key: 'rule',
			label: 'By rule',
			render: (row: PreviewRow) => {
				const rule = rules.find(
					(candidate) => candidate.id === row.ruleId,
				);
				return rule ? (
					<span className="small">{ruleSentence(rule, labelOf)}</span>
				) : (
					<span className="mut">—</span>
				);
			},
		},
	];

	const claimed = rows.filter((row) => row.classification).length;

	/* Blocked only when there were rules to choose and every one was
     unticked — never when the firm simply has none. Both the selection record
     and the run that reads it treat "no rules recorded" as "every active
     rule", which is right for a file nobody reviewed and wrong as a way to
     ask for no classification at all: saving an empty selection here would
     start a run that applies all four rules while the screen showed none.
     A firm with no published rules hits neither, and must not be stopped —
     a file confirmed for mapping with no way to start processing is the
     deadlock this flow was rebuilt to remove. */
	const emptiedSelection =
		chosen !== null && chosen.size === 0 && scope.length > 0;

	return (
		<div className="stack">
			<PreparingRowsDialog
				open={preparing}
				runId={normaliseRunId}
				fileName={file.data.originalName}
				fileHref={link(`/intake/clients/${clientId}/files/${fileId}`)}
				filesHref={link(`/intake/clients/${clientId}`)}
			/>
			<PageHeader
				breadcrumbs={[
					...clientCrumbs,
					{
						label: file.data.originalName,
						to: `/intake/clients/${clientId}/files/${fileId}`,
					},
				]}
				title="Review rules"
				subtitle={
					<>
						{count(file.data.rowCount ?? 0)} rows will be read and
						classified · {rules.length} rule
						{rules.length === 1 ? '' : 's'} in scope
					</>
				}
				actions={
					<Button
						loading={start.isPending}
						variant="contained"
						size="small"

						disabled={
							preparing ||
							start.isPending ||
							preview.isFetching ||
							emptiedSelection
						}
						title={
							emptiedSelection
								? 'Tick at least one rule. Saving an empty selection would run every rule instead of none.'
								: undefined
						}
						onClick={() => startProcessing()}
					>
						Process file
					</Button>
				}
			/>

			<div className="pagebar">
				<span className="small mut">
					The mapping is confirmed. Nothing has been written yet.
				</span>
			</div>

			<ErrorNotice error={start.error} />
			<ErrorNotice error={preview.error} />

			<Card
				title="Rules that will run"
				hint="Unticking a rule leaves the rows it would have claimed unclassified."
			>
				{rules.length === 0 ? (
					<Note
						tone="warn"
						title="No published classification rules"
						row
					>
						Every row will be left unclassified. Processing can
						still go ahead.
					</Note>
				) : (
					<div className="stack2">
						{rules.map((rule) => (
							<label key={rule.id} className="row spread up-item">
								<span
									className="row"
									style={{ gap: 'var(--s2)' }}
								>
									<input
										type="checkbox"
										checked={chosen?.has(rule.id) ?? false}
										onChange={(event) =>
											setChosen((current) => {
												const next = new Set(
													current ?? [],
												);
												if (event.target.checked)
													next.add(rule.id);
												else next.delete(rule.id);
												return next;
											})
										}
									/>
									<span className="stack2">
										<span className="b">
											{ruleSentence(rule, labelOf)}{' '}
											<span className="mut">→</span>{' '}
											<StatusBadge
												group="classification"
												value={rule.classification}
											/>
										</span>
										<span className="l2 mut">
											Priority {rule.priority} ·{' '}
											{rule.fkClientId
												? 'This client only'
												: 'Every client'}
										</span>
									</span>
								</span>
								<span className="small mut">
									{rule.matchCount} of {rows.length} sampled
								</span>
							</label>
						))}
					</div>
				)}
			</Card>

			<Card
				title="How these rules read your rows"
				hint={`${claimed} of ${rows.length} sampled rows were claimed by a rule`}
			>
				<div className="stack">
					{emptiedSelection && (
						<Note tone="warn" title="No rule is ticked" row>
							Nothing can be processed in this state. An empty
							selection is recorded as “no selection”, which the
							run reads as every rule — so it would classify the
							rows by all of them while this screen showed none.
							Tick the rules that should run.
						</Note>
					)}
					{asking && rows.length > 0 && claimed === 0 && (
						<Note
							tone="warn"
							title="No sampled row matched any rule"
							row
						>
							Either these rules do not describe this file, or the
							sample does not contain the rows they target.
							Processing will leave every row unclassified until
							the rules are corrected.
						</Note>
					)}
					{/* Fewer rows came back than were asked for — the sample IS the
              whole file, not a slice of it. Compared against `askedFor`
              rather than a hardcoded number: the endpoint asks for
              `PREVIEW_SAMPLE_ROWS` rows (`classificationService.ts`), and a
              fixed 20 here warned on a 15-row file but stayed silent on a
              30-row one even though that sample was the entire file too. */}
					{rows.length > 0 &&
						askedFor > 0 &&
						rows.length < askedFor && (
							<Note
								tone="info"
								title={`Only ${rows.length} rows were available to sample`}
								row
							>
								A small sample can agree with the rules by
								accident. The real split across every row
								appears on the file once processing finishes.
							</Note>
						)}
					{preview.isFetching ? (
						<LoadingState label="Loading sample rows…" />
					) : (
						<DataTable
							rows={rows}
							columns={tableColumns}
							rowKey={(row) =>
								String(row.values[1] ?? rows.indexOf(row))
							}
							caption={`The first ${rows.length} rows of ${file.data.originalName}`}
							empty="No rows could be read from this file."
						/>
					)}
				</div>
			</Card>
		</div>
	);
}
